import { Button } from "@/components/ui/button";
import { Shield, Award, Leaf, Building2, FileCheck, Users } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SEOHead from "@/components/SEOHead";
import { breadcrumbSchema } from "@/lib/schema";
import FinalCTA from "@/components/home/FinalCTA";
import { useQuoteModal } from "@/hooks/useQuoteModal";

const stats = [
  { value: "15+", label: "Years" },
  { value: "40,000+", label: "Carriers" },
  { value: "98%", label: "On-Time" },
  { value: "<2hr", label: "Quote Response" },
  { value: "24/7", label: "Support" },
];

const values = [
  "Reliability over promises.",
  "Relationships over transactions.",
  "Transparency over surprises.",
  "Technology that serves people.",
];

const credentials = [
  { icon: FileCheck, title: "FMCSA Licensed", detail: "MC# and DOT# - fully licensed freight broker and motor carrier authority.", color: "text-blue-500", bg: "bg-blue-500/10" },
  { icon: Shield, title: "Insured & Bonded", detail: "$1M+ cargo insurance, $75K freight broker bond, auto liability coverage on file.", color: "text-emerald-500", bg: "bg-emerald-500/10" },
  { icon: Award, title: "TIA Member", detail: "Transportation Intermediaries Association - industry gold standard for freight brokers.", color: "text-amber-500", bg: "bg-amber-500/10" },
  { icon: Leaf, title: "EPA SmartWay Partner", detail: "Committed to fuel efficiency and reduced emissions across our carrier network.", color: "text-green-500", bg: "bg-green-500/10" },
  { icon: Building2, title: "BBB Accredited", detail: "Better Business Bureau accredited with a strong rating and clean complaint history.", color: "text-sky-500", bg: "bg-sky-500/10" },
  { icon: Users, title: "FMCSA Safety Compliant", detail: "All carriers in our network maintain their own active operating authority and required insurance.", color: "text-violet-500", bg: "bg-violet-500/10" },
];

const About = () => {
  const { open } = useQuoteModal();

  return (
    <>
      <SEOHead
        title="About Varstar Alliance | Detroit Freight Brokerage"
        description="15+ years of freight experience across North America. FMCSA licensed, insured, TIA member. Detroit-based. Real people."
        canonical="/about"
      >
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]))}</script>
      </SEOHead>
      <section className="bg-foreground noise-texture pt-32 pb-20">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <FadeIn>
            <h1 className="secondary-page-title text-white mb-6 whitespace-pre-line">{"15+ years of freight expertise.\nZero years of cutting corners."}</h1>
            <p className="text-lg text-white/70">Detroit-based freight brokerage and logistics company serving shippers and carriers across the United States and Canada.</p>
          </FadeIn>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <FadeIn>
              <h2 className="section-title text-foreground mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>Varstar Alliance was founded in 2018 with a simple idea: freight logistics should be built on relationships, not runarounds. We bring 15+ years of freight experience to every shipment we manage across North America.</p>
                <p>Still headquartered in Detroit - a city built by the logistics of getting things from A to B. Our heritage shapes how we operate: hands-on, no-nonsense, and always moving forward.</p>
                <p>What started as a small brokerage has become a hybrid operation with our own fleet, a vetted 40,000+ carrier network, our freight technology platform, and a team that still believes you should get a real person when you call.</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="bg-foreground rounded-2xl p-8 flex flex-col items-center justify-center h-full min-h-[300px] noise-texture">
                <div className="text-center">
                  <p className="text-5xl font-black text-electric mb-2">2018</p>
                  <p className="text-white/70 text-sm uppercase tracking-widest font-bold">Founded in Detroit, MI</p>
                  <div className="w-12 h-px bg-electric/30 mx-auto my-6" />
                  <p className="text-white/70 text-sm leading-relaxed max-w-xs">From a single desk to a nationwide operation - still family-run, still picking up the phone.</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Trust & Credentials */}
      <section className="bg-card py-20 border-y border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <FadeIn>
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-electric mb-4 block text-center">Trust & Credentials</span>
            <h2 className="section-title text-foreground mb-4">Licensed. Insured. Verified.</h2>
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">We don't just say we're legitimate - here's the proof. Every credential publicly verifiable.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {credentials.map((c, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="border border-border rounded-xl p-6 bg-background hover:shadow-md transition-shadow h-full">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0`}>
                      <c.icon className={`w-5 h-5 ${c.color}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold leading-snug text-foreground mb-1">{c.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{c.detail}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <FadeIn><h2 className="section-title text-foreground mb-8">What We Believe</h2></FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {values.map((v, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <p className="font-bold text-foreground">{v}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-foreground noise-texture py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl lg:text-4xl font-black text-electric mb-1">{s.value}</div>
                <div className="text-xs text-white/70 uppercase tracking-wider font-semibold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Involvement */}
      <section className="bg-card border-y border-border py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <FadeIn>
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-electric mb-4 block text-center">Industry Involvement</span>
            <h2 className="section-title text-foreground mb-12">Active in the freight community.</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { org: "Transportation Intermediaries Association", role: "Member since 2018", desc: "Active participant in TIA's annual conference and policy initiatives." },
              { org: "EPA SmartWay Transport Partnership", role: "Partner", desc: "Committed to reducing transportation-related emissions and improving fuel efficiency." },
              { org: "Better Business Bureau", role: "Accredited Business", desc: "Maintaining high standards of trust and customer service in the freight industry." },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="p-6 rounded-xl border border-border bg-background">
                  <h3 className="text-lg font-semibold leading-snug text-foreground mb-1">{item.org}</h3>
                  <p className="text-electric text-sm font-semibold mb-2">{item.role}</p>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
};

export default About;
